import type { AdminCorrectionRequest, AdminDevice, AdminDeviceCreate, AdminDeviceRecord, AdminDeviceSyncHistoryPage, AdminDeviceUpdate, AdminIdentityAuditRow, AdminMetrics, AdminOrderPage, AdminOrderQuery, AdminSettings, AdminStoreHealth, AdminSyncHistoryRow, AdminTrendPoint, AdminUser, AdminUserCreate, AdminUserUpdate, StoreProfile } from "@shared/admin";
import type { Role } from "@shared/identity";
import type { CanonicalAuditEvent, ConflictResolutionAction, Order, OrderEvent, SalesSummary, ServerSyncConflict, SyncStatusResponse } from "@shared/contracts";

export type RemoteAdminData = {
  source: "staging-api";
  orders: Order[];
  total: number;
  metrics: AdminMetrics;
  trend: AdminTrendPoint[];
  auditRows: Array<{ id: string; orderId?: string; label: string; type: string; occurredAt: string; amountMinor: number; actorId: string; reason?: string }>;
  hasMore: boolean;
  nextOffset: number | null;
};

export type RemoteAdminOrderDetail = {
  order: Order;
  events: OrderEvent[];
};

function queryString(query: AdminOrderQuery): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined && value !== "") params.set(key, String(value));
  }
  return params.toString();
}

async function getJson<T>(url: string, fetcher: typeof fetch): Promise<T | null> {
  const response = await fetcher.call(globalThis, url, { credentials: "same-origin" });
  if (response.status === 401 || response.status === 403 || response.status === 503) return null;
  if (!response.ok) throw new Error(`Admin API request failed (${response.status})`);
  return (await response.json()) as T;
}

function metricsFromSummary(summary: SalesSummary): AdminMetrics {
  return {
    grossSalesMinor: summary.totalMinor,
    netSalesMinor: summary.netMinor ?? summary.totalMinor - (summary.refundMinor ?? 0),
    activeOrderCount: summary.orderCount,
    cashSalesMinor: summary.byPaymentMethod.cash.totalMinor,
    cashOrderCount: summary.byPaymentMethod.cash.orderCount,
    cardSalesMinor: summary.byPaymentMethod.card.totalMinor,
    cardOrderCount: summary.byPaymentMethod.card.orderCount,
    mixedSalesMinor: summary.byPaymentMethod.mixed.totalMinor,
    mixedOrderCount: summary.byPaymentMethod.mixed.orderCount,
    refundSalesMinor: summary.refundMinor ?? 0,
    refundCount: summary.refundCount ?? 0,
  };
}

function auditRowsFromEvents(events: CanonicalAuditEvent[]): RemoteAdminData["auditRows"] {
  return events.map((event) => {
    let payload: { orderId?: string; amountMinor?: number; reason?: string } = {};
    try { payload = JSON.parse(event.payloadJson) as typeof payload; } catch { /* keep an auditable row even if payload is malformed */ }
    return {
      id: String(event.eventId),
      orderId: payload.orderId,
      label: event.eventType,
      type: event.eventType,
      occurredAt: event.occurredAt,
      amountMinor: Number(payload.amountMinor ?? 0),
      actorId: event.actorId ?? "unknown",
      reason: payload.reason,
    };
  });
}

export async function fetchRemoteAdminData(
  query: AdminOrderQuery,
  fetcher: typeof fetch = fetch,
  granularity: "hourly" | "daily" | "weekly" | "monthly" = "daily",
): Promise<RemoteAdminData | null> {
  if (!query.from || !query.to) return null;
  const suffix = queryString(query);
  const [page, summary, audit, trend] = await Promise.all([
    getJson<AdminOrderPage>(`/api/admin/orders?${suffix}`, fetcher),
    getJson<SalesSummary>(`/api/reports/summary?from=${encodeURIComponent(query.from)}&to=${encodeURIComponent(query.to)}`, fetcher),
    getJson<{ events: CanonicalAuditEvent[] }>("/api/audit/events?limit=100", fetcher),
    getJson<{ trend: AdminTrendPoint[] }>(`/api/admin/trend?${suffix}&granularity=${granularity}`, fetcher),
  ]);
  if (!page || !summary || !audit) return null;
  return {
    source: "staging-api",
    orders: page.orders,
    total: page.total,
    metrics: metricsFromSummary(summary),
    trend: trend?.trend ?? [],
    auditRows: auditRowsFromEvents(audit.events),
    hasMore: page.hasMore,
    nextOffset: page.nextOffset,
  };
}

export async function fetchRemoteAdminOrderDetail(orderId: string, fetcher: typeof fetch = fetch): Promise<RemoteAdminOrderDetail | null> {
  if (!orderId) throw new Error("orderId is required");
  const result = await getJson<RemoteAdminOrderDetail>(`/api/admin/orders/${encodeURIComponent(orderId)}`, fetcher);
  return result;
}

export async function applyRemoteOrderCorrection(orderId: string, input: AdminCorrectionRequest, fetcher: typeof fetch = fetch): Promise<Order | null> {
  const response = await fetcher.call(globalThis, `/api/admin/orders/${encodeURIComponent(orderId)}/corrections`, {
    method: "POST",
    credentials: "same-origin",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(input),
  });
  if (response.status === 401 || response.status === 403 || response.status === 503) return null;
  if (!response.ok) {
    const body = await response.json().catch(() => ({})) as { error?: string };
    throw new Error(body.error ?? `Admin correction failed (${response.status})`);
  }
  return ((await response.json()) as { order: Order }).order;
}

export async function fetchRemoteOrdersCsv(query: AdminOrderQuery, fetcher: typeof fetch = fetch): Promise<string | null> {
  const response = await fetcher.call(globalThis, `/api/admin/export/orders.csv?${queryString(query)}`, { credentials: "same-origin" });
  if (response.status === 401 || response.status === 403 || response.status === 503) return null;
  if (!response.ok) throw new Error(`Admin export failed (${response.status})`);
  return response.text();
}

export async function fetchRemoteOrdersExcel(query: AdminOrderQuery, fetcher: typeof fetch = fetch): Promise<string | null> {
  const response = await fetcher.call(globalThis, `/api/admin/export/orders.xls?${queryString(query)}`, { credentials: "same-origin" });
  if (response.status === 401 || response.status === 403 || response.status === 503) return null;
  if (!response.ok) throw new Error(`Admin Excel export failed (${response.status})`);
  return response.text();
}

export async function fetchRemoteStoreProfile(fetcher: typeof fetch = fetch): Promise<StoreProfile | null> {
  const result = await getJson<{ profile: StoreProfile }>("/api/admin/store-profile", fetcher);
  return result?.profile ?? null;
}

export async function updateRemoteStoreProfile(profile: StoreProfile, fetcher: typeof fetch = fetch): Promise<StoreProfile | null> {
  const response = await fetcher.call(globalThis, "/api/admin/store-profile", {
    method: "PATCH",
    credentials: "same-origin",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ name: profile.name, region: profile.region, regionCode: profile.regionCode, contactPhone: profile.contactPhone, contactPerson: profile.contactPerson, address: profile.address, timeZone: profile.timeZone, currency: profile.currency, businessDayStartMinutes: profile.businessDayStartMinutes, weekStartsOn: profile.weekStartsOn }),
  });
  if (response.status === 401 || response.status === 403 || response.status === 503) return null;
  if (!response.ok) throw new Error(`Store profile update failed (${response.status})`);
  return ((await response.json()) as { profile: StoreProfile }).profile;
}

export async function fetchRemoteUsers(fetcher: typeof fetch = fetch): Promise<AdminUser[] | null> {
  const result = await getJson<{ users: AdminUser[] }>("/api/admin/users", fetcher);
  return result?.users ?? null;
}

export async function fetchRemoteDevices(fetcher: typeof fetch = fetch): Promise<{ asOf: string; devices: AdminDeviceRecord[] } | null> {
  return getJson<{ asOf: string; devices: AdminDeviceRecord[] }>("/api/admin/devices", fetcher);
}

export async function fetchRemoteStoreHealth(fetcher: typeof fetch = fetch): Promise<AdminStoreHealth[] | null> {
  const result = await getJson<{ stores: AdminStoreHealth[] }>("/api/admin/health/stores", fetcher);
  return result?.stores ?? null;
}

export async function createRemoteUser(input: AdminUserCreate, fetcher: typeof fetch = fetch): Promise<string | null> {
  const response = await fetcher.call(globalThis, "/api/admin/users", { method: "POST", credentials: "same-origin", headers: { "content-type": "application/json" }, body: JSON.stringify(input) });
  if (response.status === 401 || response.status === 403 || response.status === 503) return null;
  if (!response.ok) throw new Error(`User creation failed (${response.status})`);
  return ((await response.json()) as { userId: string }).userId;
}

export async function updateRemoteUser(userId: string, update: AdminUserUpdate, fetcher: typeof fetch = fetch): Promise<boolean> {
  const response = await fetcher.call(globalThis, `/api/admin/users/${encodeURIComponent(userId)}`, {
    method: "PATCH",
    credentials: "same-origin",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(update),
  });
  if (response.status === 401 || response.status === 403 || response.status === 503) return false;
  if (!response.ok) throw new Error(`User update failed (${response.status})`);
  return true;
}

export async function deleteRemoteUser(userId: string, fetcher: typeof fetch = fetch): Promise<boolean> {
  const response = await fetcher.call(globalThis, `/api/admin/users/${encodeURIComponent(userId)}`, { method: "DELETE", credentials: "same-origin" });
  if (response.status === 401 || response.status === 403 || response.status === 503) return false;
  if (!response.ok) throw new Error(`User deactivation failed (${response.status})`);
  return true;
}

export async function createRemoteDevice(input: AdminDeviceCreate, fetcher: typeof fetch = fetch): Promise<string | null> {
  const response = await fetcher.call(globalThis, "/api/admin/devices", { method: "POST", credentials: "same-origin", headers: { "content-type": "application/json" }, body: JSON.stringify(input) });
  if (response.status === 401 || response.status === 403 || response.status === 503) return null;
  if (!response.ok) throw new Error(`Device creation failed (${response.status})`);
  return ((await response.json()) as { deviceId: string }).deviceId;
}

export async function updateRemoteDevice(deviceId: string, update: AdminDeviceUpdate, fetcher: typeof fetch = fetch): Promise<boolean> {
  const response = await fetcher.call(globalThis, `/api/admin/devices/${encodeURIComponent(deviceId)}`, {
    method: "PATCH",
    credentials: "same-origin",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(update),
  });
  if (response.status === 401 || response.status === 403 || response.status === 503) return false;
  if (!response.ok) throw new Error(`Device update failed (${response.status})`);
  return true;
}

export async function deleteRemoteDevice(deviceId: string, fetcher: typeof fetch = fetch): Promise<boolean> {
  const response = await fetcher.call(globalThis, `/api/admin/devices/${encodeURIComponent(deviceId)}`, { method: "DELETE", credentials: "same-origin" });
  if (response.status === 401 || response.status === 403 || response.status === 503) return false;
  if (!response.ok) throw new Error(`Device deactivation failed (${response.status})`);
  return true;
}

export async function fetchRemoteSettings(fetcher: typeof fetch = fetch): Promise<AdminSettings | null> {
  const result = await getJson<{ settings: AdminSettings }>("/api/admin/settings", fetcher);
  return result?.settings ?? null;
}

export async function updateRemoteSettings(settings: AdminSettings, fetcher: typeof fetch = fetch): Promise<AdminSettings | null> {
  const response = await fetcher.call(globalThis, "/api/admin/settings", {
    method: "PATCH",
    credentials: "same-origin",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ paperWidth: settings.paperWidth, printCopies: settings.printCopies, autoSync: settings.autoSync, syncIntervalSeconds: settings.syncIntervalSeconds }),
  });
  if (response.status === 401 || response.status === 403 || response.status === 503) return null;
  if (!response.ok) throw new Error(`Admin settings update failed (${response.status})`);
  return ((await response.json()) as { settings: AdminSettings }).settings;
}

export async function fetchRemoteIdentityAudit(fetcher: typeof fetch = fetch): Promise<AdminIdentityAuditRow[] | null> {
  const result = await getJson<{ events: AdminIdentityAuditRow[] }>("/api/admin/identity-audit?limit=100", fetcher);
  return result?.events ?? null;
}

export type RemoteDeviceSyncHistory = AdminDeviceSyncHistoryPage;

export async function fetchRemoteDeviceSyncHistory(deviceId: string, page = 1, pageSize = 10, fetcher: typeof fetch = fetch): Promise<RemoteDeviceSyncHistory | null> {
  const query = new URLSearchParams({ page: String(page), limit: String(pageSize) });
  return getJson<RemoteDeviceSyncHistory>(`/api/admin/devices/${encodeURIComponent(deviceId)}/sync-history?${query}`, fetcher);
}

export async function fetchRemoteSyncStatus(fetcher: typeof fetch = fetch): Promise<SyncStatusResponse | null> {
  return getJson<SyncStatusResponse>("/api/sync/status", fetcher);
}

export async function fetchRemoteSyncConflicts(fetcher: typeof fetch = fetch): Promise<ServerSyncConflict[] | null> {
  const result = await getJson<{ conflicts: ServerSyncConflict[] }>("/api/admin/conflicts?status=open", fetcher);
  return result?.conflicts ?? null;
}

export async function resolveRemoteSyncConflict(
  conflictId: string,
  action: ConflictResolutionAction,
  reason: string,
  fetcher: typeof fetch = fetch,
): Promise<ServerSyncConflict | null> {
  const response = await fetcher.call(globalThis, `/api/admin/conflicts/${encodeURIComponent(conflictId)}/resolve`, {
    method: "POST",
    credentials: "same-origin",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ action, reason }),
  });
  if (response.status === 401 || response.status === 403 || response.status === 503) return null;
  if (!response.ok) {
    const body = await response.json().catch(() => ({})) as { error?: string };
    throw new Error(body.error ?? `Sync conflict resolution failed (${response.status})`);
  }
  return ((await response.json()) as { conflict: ServerSyncConflict }).conflict;
}
